import React, { FC } from 'react'
import times from 'lodash/times'
import CheckFilledIcon from 'transfix-icons/build/CheckFilledIcon'
import { Button } from 'transfix-ui/components/Button'
import { Box, Typography, Skeleton } from 'transfix-ui/core'
import { useMembershipContext } from 'apps/fleet-planner/context/MembershipContext'
import { useELDIntegration } from 'apps/fleet-planner/hooks'
import { Table, TableBody, TableCell, TableRow, TableHead, IconWrapper } from 'transfix-ui'

interface IIntegrationListItemProps {
  /**
   * @example 'KeepTruckin'
   */
  integrationName: string
  /**
   * Name of admin that authorized the integration
   * @example 'Gabe Kramer-Garcia'
   */
  adminName?: string
}

/**
 * Lists available carriers for integrations on Settings page
 */
const IntegrationListItem: FC<IIntegrationListItemProps> = ({ adminName, integrationName }) => {
  return (
    <Box data-test='eld-integration-list-item' display='flex' alignItems='center' height='37px'>
      <Box pr={1}>
        <IconWrapper
          color='info'
          Icon={CheckFilledIcon}
          BoxProps={{
            display: 'flex',
            alignItems: 'center',
          }}
        />
      </Box>
      <Box>
        <Typography variant='body2' component='div'>
          {integrationName} Connected
        </Typography>
        {adminName && (
          <Typography variant='body2' component='div' color='textSecondary'>
            Connected by {adminName}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

export const EldIntegrationList = () => {
  const { userMotorCarriers = [], isUserMotorCarriersLoading } = useMembershipContext()
  const { createRouteClickHandler } = useELDIntegration()

  return (
    <Box data-test='eld-integration'>
      <Table>
        {/**
         * Head
         */}
        <TableHead>
          <TableRow>
            <TableCell>Carrier</TableCell>
            <TableCell>ELD Provider</TableCell>
          </TableRow>
        </TableHead>
        {/**
         * Body
         */}
        <TableBody>
          {/**
           * Rows
           */}
          {!isUserMotorCarriersLoading &&
            userMotorCarriers.map(mc => {
              const { motorCarrier } = mc || {}
              const {
                id: carrierId,
                isKeepTruckinEnabled = false,
                keepTruckinConnector,
                name = 'No name',
              } = motorCarrier || {}
              const clickHandler = createRouteClickHandler(carrierId)
              return (
                <TableRow key={userMotorCarriers.indexOf(mc)} data-test='eld-integration-table-row'>
                  <TableCell data-test='eld-integration-table-row-name'>
                    <Typography variant='body2' component='span'>
                      {name}
                    </Typography>
                  </TableCell>
                  <TableCell data-test='eld-integration-table-row-button'>
                    {isKeepTruckinEnabled ? (
                      <IntegrationListItem
                        integrationName='KeepTruckin'
                        adminName={keepTruckinConnector}
                      />
                    ) : (
                      <Button
                        color='primary'
                        size='medium'
                        onClick={clickHandler}
                        data-test='eld-connect-button'>
                        Connect ELD
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          {/**
           * Loading state
           */}
          {isUserMotorCarriersLoading &&
            times(2).map(item => (
              <TableRow key={item} data-test='eld-integration-table-row'>
                <TableCell data-test='eld-integration-table-row-name'>
                  <Skeleton variant='rectangular' height='19px' width='124px' />
                </TableCell>
                <TableCell data-test='eld-integration-table-row-button'>
                  <Skeleton variant='rectangular' height='37px' width='229px' />
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </Box>
  )
}
